import CarsInNegotationList from '../components/CarsInNegotiation/CarsInNegotiationList';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CarsInNegotiationDetailsDealerBuyer from '@common/pages/Dashboard/components/CarsInNegotiation/LiveAuction/CarsInNegotiationDetailsDealerBuyer';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from '../../../utils/constants/routes';
import CarsInNegotiationDetailsSeller from '@common/pages/Dashboard/components/CarsInNegotiation/LiveAuction/CarsInNegotiationDetailsSeller';
import {
  useLazyGetBiddersNegotiationListBuyNowQuery,
  useLazyGetBiddersNegotiationListQuery,
  useLazyGetOthersCarsInNegotiationBuyNowQuery,
  useLazyGetOthersCarsInNegotiationQuery,
  useLazyGetOwnCarsInNegotiationBuyNowQuery,
  useLazyGetOwnCarsInNegotiationQuery,
} from '../../../toolkit/carsInNegotiation/carsInNegotiationApi';

const CarsInNegotiation = () => {
  // React Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Component state
  const [selectedCar, setSelectedCar] = useState(null);

  // Extract tab state from URL search params (0 = My Cars, 1 = Other Cars)
  const myCarsOrOtherCars = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    return tab !== null ? parseInt(tab, 10) : null;
  }, [location.search]);

  // Determine if current route is BuyNow or LiveAuction
  const isBuyNowRoute = useMemo(() => {
    const subRoute = location?.pathname?.split?.(`${routes?.dashboardNavigation}/`)?.[1]?.split?.(`/`)?.[0];
    return subRoute === routes?.carsInNegotiationBuyNow?.split?.('/')?.[1];
  }, [location.pathname]);

  // Extract vehicle ID from pathname
  const vehicleId = useMemo(() => {
    const pathParts = isBuyNowRoute
      ? location.pathname.split(`${routes.carsInNegotiationBuyNow}/`)
      : location.pathname.split(`${routes.carsInNegotiation}/`);
    return pathParts.length > 1 ? pathParts[1] : null;
  }, [location.pathname, isBuyNowRoute]);

  // API hooks
  const [getOwnCarsInNegotiationApiHandler] = useLazyGetOwnCarsInNegotiationQuery();
  const [getOwnCarsInNegotiationBuyNowApiHandler] = useLazyGetOwnCarsInNegotiationBuyNowQuery();
  const [getOthersCarsInNegotiationApiHandler] = useLazyGetOthersCarsInNegotiationQuery();
  const [getOthersCarsInNegotiationBuyNowApiHandler] = useLazyGetOthersCarsInNegotiationBuyNowQuery();
  const [getBiddersNegotiationListApiHandler, getBiddersNegotiationListApiData] =
    useLazyGetBiddersNegotiationListQuery();
  const [getBiddersNegotiationListBuyNowApiHandler, getBiddersNegotiationListBuyNowApiData] =
    useLazyGetBiddersNegotiationListBuyNowQuery();

  // Redirect to base route if myCarsOrOtherCars is not set when trying to view vehicle detail
  useEffect(() => {
    if (myCarsOrOtherCars === null && vehicleId) {
      const baseRoute = isBuyNowRoute
        ? `${routes.dashboardNavigation}${routes.carsInNegotiationBuyNow}`
        : `${routes.dashboardNavigation}${routes.carsInNegotiation}`;
      navigate(baseRoute);
    }
  }, [myCarsOrOtherCars, vehicleId, isBuyNowRoute, navigate]);

  // Handler for car click - memoized to prevent unnecessary re-renders
  const handleCarClick = (car) => {
    setSelectedCar(car);
    // Preserve the tab parameter when navigating to detail view
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    const baseRoute = isBuyNowRoute
      ? `${routes.dashboardNavigation}${routes.carsInNegotiationBuyNow}`
      : `${routes.dashboardNavigation}${routes.carsInNegotiation}`;
    navigate(`${baseRoute}/${car?.Vehicle_Id}${queryString ? `?${queryString}` : ''}`);
  };

  // Handler for cars filter change - memoized to prevent unnecessary re-renders
  const handleMyCarsOrOtherCars = useCallback(
    (value) => {
      // Update URL search params instead of local state
      const baseRoute = isBuyNowRoute
        ? `${routes.dashboardNavigation}${routes.carsInNegotiationBuyNow}`
        : `${routes.dashboardNavigation}${routes.carsInNegotiation}`;
      navigate(`${baseRoute}?tab=${value}`, { replace: true });
    },
    [navigate, isBuyNowRoute]
  );

  // Determine pagination handler based on route and tab
  const paginationHandler = useMemo(() => {
    if (isBuyNowRoute) {
      return myCarsOrOtherCars === 0
        ? getOwnCarsInNegotiationBuyNowApiHandler
        : getOthersCarsInNegotiationBuyNowApiHandler;
    }
    return myCarsOrOtherCars === 0 ? getOwnCarsInNegotiationApiHandler : getOthersCarsInNegotiationApiHandler;
  }, [
    isBuyNowRoute,
    myCarsOrOtherCars,
    getOwnCarsInNegotiationApiHandler,
    getOthersCarsInNegotiationApiHandler,
    getOwnCarsInNegotiationBuyNowApiHandler,
    getOthersCarsInNegotiationBuyNowApiHandler,
  ]);

  // Render detail views when vehicle ID is present
  if (vehicleId && myCarsOrOtherCars !== null ) {
    if (myCarsOrOtherCars === 0) {
      return (
        <CarsInNegotiationDetailsSeller
          getBiddersNegotiationListApiHandler={
            isBuyNowRoute ? getBiddersNegotiationListBuyNowApiHandler : getBiddersNegotiationListApiHandler
          }
          getBiddersNegotiationListApiData={
            isBuyNowRoute ? getBiddersNegotiationListBuyNowApiData : getBiddersNegotiationListApiData
          }
        />
      );
    }

    return (
      <CarsInNegotiationDetailsDealerBuyer
        Bid_Id={selectedCar?.bid_id || selectedCar?.negotiation?.bid_id}
        selectedCar={selectedCar}
        setSelectedCar={setSelectedCar}
        myCarsOrOtherCars={myCarsOrOtherCars}
      />
    );
  }

  // Default: render list view
  return (
    <CarsInNegotationList
      paginationHandler={paginationHandler}
      selectedCar={selectedCar}
      setSelectedCar={setSelectedCar}
      onCarClick={handleCarClick}
      onMyCarsOrOtherCars={handleMyCarsOrOtherCars}
    />
  );
};

export default CarsInNegotiation;
